// Vault-side HTTP helpers for the DDC S3 gateway's onboarding
// endpoints. The vault-sdk has equivalent helpers but doesn't export
// them publicly; rather than reach into `dist/internal/...` we own the
// wire format here. Both endpoints sign the literal empty byte string
// — same convention vault-api uses elsewhere — and the gateway returns
// 404 when ONBOARDING_ENABLED=false (production), which the caller
// must treat as "skip the onboarding switch entirely".

import type { Signer } from '@cef-ai/signer';
import type { OnboardingStatusWithAction } from '@/lib/clients/vault/onboarding';
import { gatewayBase, signedEmptyBytesHeaders, type FetchLike } from './gateway-auth';

export type { FetchLike } from './gateway-auth';

/**
 * GET /auth/onboarding/status. Returns null when the gateway responds
 * 404 (onboarding disabled — production deployments). Throws on any
 * other non-2xx so the caller surfaces a real failure instead of
 * silently falling through.
 */
export async function getOnboardingStatusWithAction(
  s3Endpoint: string,
  signer: Signer,
  fetchImpl: FetchLike = fetch,
): Promise<OnboardingStatusWithAction | null> {
  const url = `${gatewayBase(s3Endpoint)}/auth/onboarding/status`;
  const r = await fetchImpl(url, {
    method: 'GET',
    headers: await signedEmptyBytesHeaders(signer),
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`onboarding status ${r.status}`);
  return (await r.json()) as OnboardingStatusWithAction;
}

/**
 * POST /auth/onboarding/bootstrap. Accepts any 2xx:
 *   - 200 full success
 *   - 202 accepted / still seeding asynchronously (the dev gateway's current
 *     behavior — seeding the faucet + DDC deposit is not instantaneous)
 *   - 207 partial (the body's `errors` array describes what didn't seed)
 *
 * We deliberately do NOT branch on which 2xx it was, and don't surface the
 * body: the caller is a polling orchestrator (`runOnboardingFlow`) whose
 * `bootstrap` case does `await bootstrap(); continue;` — the next iteration
 * re-pulls `/status` and re-reads `next_user_action`, so "accepted but not
 * finished yet" resolves itself by looping, bounded by `maxIterations`. The
 * status code only has to tell us the request was *taken*; `/status` is the
 * source of truth for whether onboarding is done.
 *
 * Note this is deliberately laxer than the SDK's own `runBootstrap`, which
 * still rejects 202 — vault drives the onboarding switch itself, so we own
 * this policy.
 */
export async function postOnboardingBootstrap(
  s3Endpoint: string,
  signer: Signer,
  fetchImpl: FetchLike = fetch,
): Promise<void> {
  const url = `${gatewayBase(s3Endpoint)}/auth/onboarding/bootstrap`;
  const r = await fetchImpl(url, {
    method: 'POST',
    headers: await signedEmptyBytesHeaders(signer),
  });
  if (!r.ok) {
    throw new Error(`onboarding bootstrap ${r.status}`);
  }
}
